import type {
  SchemeContent,
  SchemeKnob,
  SchemeKnobValues,
  SchemeManifest,
} from "@shared/types/foyer";
import { SUB_TOGGLE, type TemplateSubSection } from "../PremiseSidebar";

/**
 * The model behind Template → Screens.
 *
 * The manifest declares knobs; this turns them into the list an operator
 * actually thinks in: "what does the TV show, where, and in what order". Two
 * joins do the work:
 *
 *  1. An `order` knob's items are screen ids, and a screen toggle names the
 *     screen it controls (`controls: { kind: "screen", id }`). Joining them
 *     puts the switch and the reorder arrows on the SAME row, instead of a
 *     column of switches next to a separate list of names.
 *  2. `SUB_TOGGLE` maps a Component tab to its owning knob. Reversed, it tells
 *     each row which editor to link to - so "turn it on" and "fill it in" stop
 *     living in different parts of the app.
 */

/** Knob id → the Component tab that edits that screen's content. */
export const KNOB_SUB: Record<string, TemplateSubSection> = Object.fromEntries(
  Object.entries(SUB_TOGGLE).map(([sub, knobId]) => [
    knobId,
    sub as TemplateSubSection,
  ])
);

/** Screen ids that have an editor tab but no toggle of their own. */
const SCREEN_SUB: Record<string, TemplateSubSection> = {
  apps: "apps",
  radio: "radio",
  info: "info",
  menu: "menu",
  wifi: "wifi",
  welcome: "welcome",
  relatives: "relatives",
  instagram: "instagram",
  weather: "weather",
};

/**
 * Where a knob group lands on the TV. The manifests use several names for the
 * same two places (a hotel template says "Sections"/"Main menu", care says
 * "Main menu"); operators only care about the two destinations, so they merge.
 */
const GROUP_TITLES: Record<string, string> = {
  Sections: "Home screen",
  "Main menu": "Home screen",
  "Home screen": "Home screen",
  Layout: "Home screen",
  Information: "Inside Information",
};

const GROUP_HINTS: Record<string, string> = {
  "Home screen": "Tiles on the first screen a guest sees, in this order.",
  "Inside Information":
    "Reached from the Information tile - not shown on the home screen.",
};

/** The rename control a renameable screen's card carries. */
export interface ScreenTitleField {
  /** The `text` knob that holds the name. */
  knobId: string;
  label: string;
  help?: string;
  /** Shown in an empty field. */
  placeholder: string;
  /** The name the screen falls back to when nothing is typed. */
  defaultName: string;
  maxLength?: number;
  /** Exactly what is stored (may be ""), so the field stays clearable. */
  raw: string;
}

export interface ScreenRow {
  /** Stable React key. */
  key: string;
  /** What the screen is CALLED - the operator's title when it has one. */
  label: string;
  help?: string;
  /** The toggle knob that switches this row on/off; absent = not switchable. */
  knobId?: string;
  /** Resolved on/off. Rows with no knob are always on. */
  on: boolean;
  /** Why this row has no switch (shown in place of one). */
  fixedNote?: string;
  /** Component tab that edits this screen's content, when it has one. */
  sub?: TemplateSubSection;
  /** True when the row participates in the group's order knob. */
  orderable: boolean;
  /** The order knob whose array this row currently sits in. */
  orderKnobId?: string;
  /**
   * The other zone this screen is allowed to move to, or undefined when the
   * manifest pins it here. A screen is movable exactly when a SECOND order
   * knob's `items` also declare it - so pinning one is a one-line manifest
   * decision, not a special case in the UI.
   */
  moveTo?: { orderKnobId: string; title: string };
  /** Present when this screen is one the operator names themselves. */
  title?: ScreenTitleField;
}

export interface ScreenGroup {
  title: string;
  hint?: string;
  /** The `order` knob whose array this group's orderable rows reorder. */
  orderKnobId?: string;
  rows: ScreenRow[];
  /** Knobs that aren't screens at all (variants etc.) - rendered as-is. */
  extras: SchemeKnob[];
}

/** Effective value of a toggle knob: stored value, else the manifest default. */
function resolveToggle(
  knob: Extract<SchemeKnob, { type: "toggle" }>,
  values: SchemeKnobValues | undefined
): boolean {
  const v = values?.[knob.id];
  return typeof v === "boolean" ? v : knob.default;
}

/**
 * Effective value of ONE order knob: the stored array when there is one, else
 * the manifest default, in both cases filtered to the ids the manifest still
 * declares (so removing a screen from a manifest self-heals every premise's
 * stored value with no migration).
 *
 * Deliberately identical to the TV's `knobs.order()`. In particular it does NOT
 * top the stored array up with the manifest default: once membership of these
 * arrays IS the screen's placement, "the operator took menu out of infoOrder"
 * and "menu was added to the manifest after this premise last saved" look the
 * same from inside one knob, and re-adding the default would make a move
 * impossible to save. {@link resolveOrders} tells them apart - by looking at
 * whether the id turned up in the OTHER zone.
 */
export function resolveOrder(
  knob: Extract<SchemeKnob, { type: "order" }>,
  values: SchemeKnobValues | undefined
): string[] {
  const v = values?.[knob.id];
  const declared = new Set(knob.items.map((it) => it.id));
  const chosen = Array.isArray(v)
    ? v.filter((x): x is string => typeof x === "string")
    : knob.default;
  return chosen.filter((id) => declared.has(id));
}

/**
 * The rename field a toggle knob owns, resolved against what the premise
 * stored. Returns undefined for the vast majority of screens - the ones the
 * template names itself.
 */
function titleField(
  toggle: SchemeKnob | undefined,
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined
): ScreenTitleField | undefined {
  if (!toggle || toggle.type !== "toggle" || !toggle.titleKnobId) return;
  const knob = manifest.knobs.find((k) => k.id === toggle.titleKnobId);
  if (!knob || knob.type !== "text") return;
  const stored = values?.[knob.id];
  return {
    knobId: knob.id,
    label: knob.label,
    help: knob.help,
    placeholder: knob.placeholder ?? knob.default,
    defaultName: knob.default,
    maxLength: knob.maxLength,
    raw: typeof stored === "string" ? stored : "",
  };
}

/** The screen's name as everything else must show it: what was typed, else the
 *  manifest default (never blank - see `textValue`). */
function titledLabel(
  fallback: string,
  title: ScreenTitleField | undefined
): string {
  if (!title) return fallback;
  return title.raw.trim() || title.defaultName;
}

/** Text knobs that a toggle already owns as its title - they are rendered ON
 *  that screen's card, so they must not also fall through to `extras`. */
function titleKnobIds(manifest: SchemeManifest): Set<string> {
  const out = new Set<string>();
  for (const k of manifest.knobs) {
    if (k.type === "toggle" && k.titleKnobId) out.add(k.titleKnobId);
  }
  return out;
}

/**
 * Screens that exist but are deliberately not switchable, keyed by screen id.
 * Today only video calling: the server owns that answer (premise policy +
 * resident seat), so a switch here could only ever contradict it (VC2-8).
 */
const FIXED_NOTES: Record<string, string> = {
  videocall: "Follows the resident's seat - no switch",
};

/**
 * `showOffers`/`showPromotions` are gated by the Promotions LICENSE, not only
 * by their own knob - the CMS Products page decides whether this property
 * sells anything at all (docs/memory/features/room-offers.md). Mirrors
 * `PROMOTIONS_LICENSED_KNOBS` in the TV's `schemeContext.ts`; the two live
 * apart because the CMS resolves its own knobs independently of the TV
 * bundle (`resolveToggle` above, not `makeKnobAccessors`).
 */
const PROMOTIONS_LICENSED_KNOBS = new Set(["showOffers", "showPromotions"]);

/** Shown ON the Offers row once the license makes it un-switchable. */
const OFFERS_INCLUDED_NOTE = "Included with the Promotions product";

/**
 * Knobs that stop being switchable while a certified app is ON (NFLX-4).
 * Mirrors `CERTIFIED_LOCKED_ON_KNOBS` in the TV's `schemeContext.ts`, for the
 * same reason the promotions set is mirrored: the CMS resolves its own knobs
 * and never loads the TV bundle. Keep the two in step.
 */
const CERTIFIED_LOCKED_ON_KNOBS = new Set(["showApps"]);

/** Appended to a group's hint once licensing hid a row out of it, so an
 *  operator who came looking for "Offers" or "Promotion" learns where it
 *  went instead of concluding the board is broken. */
const PROMOTIONS_LICENSE_HINT =
  "Offers and the Promotion banner need the Promotions product - add it on this place's Products page.";

/** Where a group's rows land on the TV, from the manifest's own group word. */
export function zoneTitle(rawName: string | undefined): string {
  return GROUP_TITLES[rawName ?? ""] ?? rawName ?? "Options";
}

type OrderKnob = Extract<SchemeKnob, { type: "order" }>;

/**
 * The manifest's order knobs, resolved in the order they must be READ.
 *
 * Placement is membership of these arrays, so an id that (through stale
 * storage) sits in two of them would otherwise render as two cards for one
 * screen. Information is visited first, which makes "Information wins a tie" -
 * the same tie-break the TV applies in `screenPlacement`, and the reason
 * re-declaring a reclassified id in `homeOrder.items` cannot resurrect it as a
 * tile on a premise that saved the array years ago.
 */
function orderKnobs(manifest: SchemeManifest): OrderKnob[] {
  return manifest.knobs
    .filter((k): k is OrderKnob => k.type === "order")
    .sort(
      (a, b) =>
        (zoneTitle(a.group) === "Inside Information" ? 0 : 1) -
        (zoneTitle(b.group) === "Inside Information" ? 0 : 1)
    );
}

/**
 * Every order knob resolved TOGETHER - the CMS's mirror of the TV's
 * `screenPlacement`, and the only correct way to read placement.
 *
 * Two passes:
 *  1. each knob's own resolved array, Information first, with an id already
 *     claimed by an earlier knob dropped (Information wins a tie, so a stale
 *     `homeOrder` carrying a since-reclassified id cannot resurrect it);
 *  2. any id a knob DECLARES but that landed in no zone at all is appended to
 *     that knob - which is how a screen added to the manifest after a premise
 *     last saved its order still shows up, without that rule swallowing a
 *     deliberate move out of the zone.
 */
export function resolveOrders(
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined
): Record<string, string[]> {
  const knobs = orderKnobs(manifest);
  const out: Record<string, string[]> = {};
  const placed = new Set<string>();
  for (const knob of knobs) {
    out[knob.id] = resolveOrder(knob, values).filter((id) => {
      if (placed.has(id)) return false;
      placed.add(id);
      return true;
    });
  }
  // An id declared by BOTH zones needs a tie-break of its own here: its
  // manifest `default` is the zone it was designed to start in (Anhöriga is a
  // home tile that MAY be pushed under Information, not the reverse), so that
  // decides before the bare `items` fallback does.
  for (const knob of knobs) {
    for (const id of knob.default) {
      if (placed.has(id) || !knob.items.some((it) => it.id === id)) continue;
      placed.add(id);
      out[knob.id].push(id);
    }
  }
  for (const knob of knobs) {
    for (const item of knob.items) {
      if (placed.has(item.id)) continue;
      placed.add(item.id);
      out[knob.id].push(item.id);
    }
  }
  return out;
}

export function buildScreenGroups(
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined,
  opts: {
    /**
     * Defaults to licensed - every existing caller/test that predates this
     * gate and has nothing to do with licensing keeps behaving exactly as
     * before. `ScreensEditor` always passes the premise's real answer.
     */
    hasPromotions?: boolean;
    /**
     * Is a certified app switched on for this premise (NFLX-4)? Defaults to
     * FALSE - not knowing must never take a switch away from an operator.
     */
    appsCertified?: boolean;
    /** What requires it, and how to release it, for the fixed note. */
    certificationNote?: string;
  } = {}
): ScreenGroup[] {
  const hasPromotions = opts.hasPromotions ?? true;
  const appsCertified = opts.appsCertified ?? false;
  const groups: ScreenGroup[] = [];
  const groupFor = (rawName: string | undefined) => {
    const title = zoneTitle(rawName);
    const found = groups.find((g) => g.title === title);
    if (found) return found;
    const created: ScreenGroup = {
      title,
      hint: GROUP_HINTS[title],
      rows: [],
      extras: [],
    };
    groups.push(created);
    return created;
  };

  // Pass 1 - order knobs define each group's skeleton, so the rows come out in
  // the order the TV renders them rather than in declaration order.
  const orders = orderKnobs(manifest);
  const resolved = resolveOrders(manifest, values);
  // Groups are CREATED in manifest declaration order (Home screen above Inside
  // Information, as the TV reads); `resolveOrders` settles ties Information
  // first. Don't collapse the two loops.
  for (const knob of manifest.knobs) {
    if (knob.type === "order") groupFor(knob.group);
  }
  const claimed = new Set<string>();
  for (const knob of orders) {
    const group = groupFor(knob.group);
    group.orderKnobId = knob.id;
    for (const id of resolved[knob.id]) {
      const item = knob.items.find((it) => it.id === id);
      const toggle = manifest.knobs.find(
        (k) =>
          k.type === "toggle" &&
          k.controls?.kind === "screen" &&
          k.controls.id === id
      );
      if (toggle && toggle.type === "toggle") claimed.add(toggle.id);
      const other = orders.find(
        (k) => k.id !== knob.id && k.items.some((it) => it.id === id)
      );
      const title = titleField(toggle, manifest, values);
      group.rows.push({
        key: id,
        label: titledLabel(toggle?.label ?? item?.label ?? id, title),
        title,
        help: toggle?.help,
        knobId: toggle?.id,
        on: toggle?.type === "toggle" ? resolveToggle(toggle, values) : true,
        fixedNote: toggle ? undefined : FIXED_NOTES[id],
        sub: (toggle && KNOB_SUB[toggle.id]) ?? SCREEN_SUB[id],
        orderable: true,
        orderKnobId: knob.id,
        moveTo: other
          ? { orderKnobId: other.id, title: zoneTitle(other.group) }
          : undefined,
      });
    }
  }

  // Pass 2 - every remaining knob, in declaration order.
  const titles = titleKnobIds(manifest);
  for (const knob of manifest.knobs) {
    if (knob.type === "order") continue;
    if (knob.type !== "toggle") {
      // A title knob is rendered ON the card of the screen it names, so it must
      // not ALSO appear as a loose control below the group.
      if (!titles.has(knob.id)) groupFor(knob.group).extras.push(knob);
      continue;
    }
    if (claimed.has(knob.id)) continue;
    const screenId = knob.controls?.kind === "screen" ? knob.controls.id : "";
    const title = titleField(knob, manifest, values);
    groupFor(knob.group).rows.push({
      key: knob.id,
      label: titledLabel(knob.label, title),
      title,
      help: knob.help,
      knobId: knob.id,
      on: resolveToggle(knob, values),
      sub: KNOB_SUB[knob.id] ?? SCREEN_SUB[screenId],
      orderable: false,
    });
  }

  // License gate, applied last so it sees every row pass 1/2 produced
  // regardless of which one built it.
  if (!hasPromotions) {
    // Unlicensed: neither row can ever be turned on, so there is nothing to
    // show a switch FOR - dropped entirely, same as a knob-off Component tab,
    // with a hint on the group explaining where they went.
    for (const g of groups) {
      const before = g.rows.length;
      g.rows = g.rows.filter(
        (r) => !PROMOTIONS_LICENSED_KNOBS.has(r.knobId ?? "")
      );
      if (g.rows.length < before) {
        g.hint = g.hint
          ? `${g.hint} ${PROMOTIONS_LICENSE_HINT}`
          : PROMOTIONS_LICENSE_HINT;
      }
    }
  } else {
    // Licensed: Offers stops being independently switchable - buying the
    // product is what turns it on. It stays on the board (still orderable,
    // still renameable) with a locked "Included" note where the switch was.
    for (const g of groups) {
      for (const r of g.rows) {
        if (r.knobId === "showOffers") {
          r.knobId = undefined;
          r.on = true;
          r.fixedNote = OFFERS_INCLUDED_NOTE;
        }
      }
    }
  }

  // Certification gate, after the license one for the same reason: it must see
  // every row whichever pass produced it. The row STAYS on the board - still
  // named, still linking into its own editor - and loses only its switch, so an
  // operator who came looking for "why can't I turn Apps off" finds the answer
  // on the row itself rather than on a support call. The note names the escape
  // hatch too, because there is one: switch the app off in Apps.
  if (appsCertified) {
    for (const g of groups) {
      for (const r of g.rows) {
        if (r.knobId && CERTIFIED_LOCKED_ON_KNOBS.has(r.knobId)) {
          r.knobId = undefined;
          r.on = true;
          r.fixedNote = opts.certificationNote ?? "Required by certification";
        }
      }
    }
  }

  return groups.filter((g) => g.rows.length > 0 || g.extras.length > 0);
}

/** screen id → the zone the premise currently places it in. */
export function placementIndex(
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined
): Map<string, string> {
  const out = new Map<string, string>();
  const resolved = resolveOrders(manifest, values);
  for (const knob of orderKnobs(manifest)) {
    for (const id of resolved[knob.id]) out.set(id, zoneTitle(knob.group));
  }
  return out;
}

/**
 * Where a Component tab's screen appears on the TV ("Home screen" / "Inside
 * Information"), and whether it is currently on. Returns null when this
 * template has no toggle for that tab - then there is nothing to state.
 *
 * Placement is read from the resolved order knobs, not from the toggle's own
 * `group`: once an operator can move a screen between zones, the manifest's
 * grouping word is only where it STARTS.
 */
export function screenState(
  sub: TemplateSubSection,
  manifest: SchemeManifest | undefined,
  values: SchemeKnobValues | undefined
): { knobId: string; label: string; on: boolean; placement?: string } | null {
  const knobId = SUB_TOGGLE[sub];
  if (!knobId || !manifest) return null;
  const knob = manifest.knobs.find((k) => k.id === knobId);
  if (!knob || knob.type !== "toggle") return null;
  const screenId = knob.controls?.kind === "screen" ? knob.controls.id : "";
  return {
    knobId,
    label: titledLabel(knob.label, titleField(knob, manifest, values)),
    on: resolveToggle(knob, values),
    placement:
      placementIndex(manifest, values).get(screenId) ??
      GROUP_TITLES[knob.group ?? ""],
  };
}

/** Move an id within an order knob's array. Returns null when it can't move. */
export function reorder(
  order: string[],
  id: string,
  dir: -1 | 1
): string[] | null {
  const i = order.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return null;
  const next = order.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** Move an id to an absolute position in its array (the drag equivalent of
 *  `reorder`). Returns null when the id isn't there or nothing would change. */
export function reorderTo(
  order: string[],
  id: string,
  index: number
): string[] | null {
  const i = order.indexOf(id);
  if (i < 0) return null;
  const without = order.filter((x) => x !== id);
  const at = Math.max(0, Math.min(index, without.length));
  if (at === i) return null;
  return [...without.slice(0, at), id, ...without.slice(at)];
}

/**
 * Move a screen into another zone: `id` joins `toKnobId` at `index` and leaves
 * every other order knob that was holding it. Returns the knob patch to save,
 * or null when the manifest does not declare the screen in the target zone
 * (which is how a pinned screen stays pinned even if the UI ever offered it).
 *
 * Both arrays are written out in full rather than sparsely, on purpose: the
 * operator has now made placement an explicit choice, so it must survive a
 * later change to either knob's manifest `default` (see the sparse-storage
 * rule in the scheme-knobs note).
 */
export function moveZone(
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined,
  id: string,
  toKnobId: string,
  index?: number
): SchemeKnobValues | null {
  const orders = orderKnobs(manifest);
  const target = orders.find((k) => k.id === toKnobId);
  if (!target || !target.items.some((it) => it.id === id)) return null;
  const resolved = resolveOrders(manifest, values);
  const next: SchemeKnobValues = { ...(values ?? {}) };
  for (const knob of orders) {
    const current = resolved[knob.id];
    if (knob.id === toKnobId) {
      const without = current.filter((x) => x !== id);
      const at =
        index == null
          ? without.length
          : Math.max(0, Math.min(index, without.length));
      next[knob.id] = [...without.slice(0, at), id, ...without.slice(at)];
    } else if (current.includes(id)) {
      next[knob.id] = current.filter((x) => x !== id);
    }
  }
  return next;
}

/**
 * One-line "is there anything in it yet" for a screen row. Deliberately terse:
 * it exists so an operator can spot the screen they switched on but never
 * filled in, without opening each tab.
 */
export function screenSummary(
  sub: string | undefined,
  content: SchemeContent | undefined
): string | undefined {
  if (!sub || !content) return undefined;
  const plural = (n: number, one: string, many = `${one}s`) =>
    `${n} ${n === 1 ? one : many}`;
  switch (sub) {
    case "apps":
      return plural(content.apps?.length ?? 0, "app");
    case "radio":
      return plural(content.stations?.length ?? 0, "station");
    case "info":
      return plural(content.infoSections?.length ?? 0, "section");
    case "menu":
      return content.menu?.source === "feed"
        ? content.menu.feed?.url
          ? "RSS feed"
          : "Feed not connected"
        : plural(content.menu?.weeks?.length ?? 0, "week");
    case "wifi":
      return content.wifi?.ssid || "Not filled in";
    case "welcome":
      return content.welcome ? "Filled in" : "Not filled in";
    case "instagram":
      return content.instagram?.handle
        ? `@${content.instagram.handle}`
        : "Not filled in";
    case "weather":
      return content.weather?.units === "f" ? "Fahrenheit" : "Celsius";
    case "relatives":
      return plural(content.relatives?.sections?.length ?? 0, "section");
    case "custom": {
      const n = content.custom?.items?.length ?? 0;
      return n === 0 ? "Not filled in" : plural(n, "item");
    }
    // Offers are the one screen whose content is NOT in `SchemeContent`: they
    // are a scoped catalogue of their own (docs/room-offers.md §8), so this line
    // says where to look rather than inventing a count it cannot read. A number
    // fetched here would also be the wrong one - the TV counts what is LIVE
    // right now, which is a different question from what is authored.
    case "offers":
      return "Authored under Offers";
    // Foyer's own player (TV-3a) follows Offers, not Custom: neither screen has
    // any content in `SchemeContent` at all. The film catalogue comes from the
    // room's content packages and the channel line-up from the premise's
    // channels, both of them collections of their own edited elsewhere in the
    // CMS - so this line says where to look rather than inventing a count it
    // cannot read. It would be the wrong count anyway: the TV shows what the
    // room is ENTITLED to right now, which is a different question from what
    // somebody authored.
    case "vod":
      return "From the room's content packages";
    case "guide":
      return "From the premise's channel line-up";
    case "slots":
      return content.slots && Object.keys(content.slots).length
        ? "Filled in"
        : "Not filled in";
    default:
      return undefined;
  }
}
